<script setup lang="ts">
import type { ComputedRef } from "vue"
import { useField } from "vee-validate"
import { computed } from "vue"

export type ICheckboxVariant = "primary" | "secondary"

interface CheckboxProps {
  name: string
  label: string
  showLabel?: boolean
  modelValue?: boolean
  error?: string
  indeterminate?: boolean
  variant?: "primary" | "secondary"
  disabled?: boolean
  value?: boolean
  hint?: string
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  showLabel: true,
  modelValue: false,
  error: "",
  indeterminate: false,
  variant: "primary",
  disabled: false,
  hint: "",
})

const emit = defineEmits(["update:modelValue", "input"])

const { value: useFieldValue, errorMessage, handleChange } = useField(props.name)

function onChecked(event: Event): void {
  if (props.disabled)
    return

  const target = event.target as HTMLInputElement

  handleChange(target.checked)
  emit("update:modelValue", target.checked)
  emit("input", event)
}

const hasErrors: ComputedRef<boolean> = computed(() => {
  return !!errorMessage.value || !!props.error
})
</script>

<template>
  <div>
    <label :for="name" class="gpx-checkbox-wrapper" :class="{ 'gpx-checkbox_disabled': disabled }">
      <input
        :id="name"
        :name="name"
        :checked="modelValue"
        :disabled="disabled"
        :value="value || useFieldValue"
        type="checkbox"
        @input="onChecked"
      >
      <span
        class="gpx-checkbox-checkmark rounded-sm"
        :class="[`gpx-checkbox-checkmark_${variant}`, { 'gpx-checkbox-checkmark_indeterminate': indeterminate }]"
      />
      <slot name="label">
        <span class="gpx-checkbox-label" :class="{ 'sr-only': !showLabel }">{{ label }}</span>
      </slot>
    </label>
    <span v-if="hint" class="gpx-checkbox-hint">{{ hint }}</span>
    <span v-if="hasErrors" class="gpx-field-error" v-text="error || errorMessage" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-checkbox-wrapper {
  --gpx-checkbox-width: 18px;
  --gpx-checkbox-height: 18px;
  --gpx-checkbox-border-width: 2px;

  display: flex;
  align-items: center;
  position: relative;
  padding-left: calc(var(--gpx-checkbox-height) + var(--gpx-checkbox-border-width) + var(--gpx-checkbox-border-width));
  min-height: calc(var(--gpx-checkbox-height) + var(--gpx-checkbox-border-width) + var(--gpx-checkbox-border-width));
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.gpx-checkbox-wrapper input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.gpx-checkbox-label {
  margin-left: 0.5rem;
  font-size: 1rem;

  @apply text-gray-800;
}

.gpx-checkbox-checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: var(--gpx-checkbox-height);
  width: var(--gpx-checkbox-width);
  border-width: var(--gpx-checkbox-border-width);
  border-style: solid;
  transition: 0.15s ease;
  box-sizing: content-box;
  background-color: white;

  @apply border-gray-500;
}

.gpx-checkbox-checkmark::after {
  content: "";
  position: absolute;
  display: none;
  box-sizing: content-box;
}

.gpx-checkbox-wrapper .gpx-checkbox-checkmark::after {
  left: 6px;
  top: 1px;
  width: 5px;
  height: 11px;
  transform: rotate(45deg);
}

.gpx-checkbox-wrapper input:checked ~ .gpx-checkbox-checkmark::after {
  display: block;
}

/* Primary variant styles */
.gpx-checkbox-wrapper:hover input ~ .gpx-checkbox-checkmark_primary,
.gpx-checkbox-wrapper input:focus ~ .gpx-checkbox-checkmark_primary {
  @apply border-primary;
}

.gpx-checkbox-wrapper input:focus ~ .gpx-checkbox-checkmark_primary {
  @apply outline-primary/20 outline-2;
}

.gpx-checkbox-wrapper input:checked ~ .gpx-checkbox-checkmark_primary {
  @apply bg-white border-primary;
}

.gpx-checkbox-checkmark_primary::after {
  border-style: solid;
  border-width: 0 var(--gpx-checkbox-border-width) var(--gpx-checkbox-border-width) 0;

  @apply border-primary;
}

/* Secondary variant styles */
.gpx-checkbox-wrapper:hover input ~ .gpx-checkbox-checkmark_secondary,
.gpx-checkbox-wrapper input:focus ~ .gpx-checkbox-checkmark_secondary {
  @apply border-secondary;
}

.gpx-checkbox-wrapper input:focus ~ .gpx-checkbox-checkmark_secondary {
  @apply outline-secondary/20 outline-2;
}

.gpx-checkbox-wrapper input:checked ~ .gpx-checkbox-checkmark_secondary {
  @apply bg-white border-secondary;
}

.gpx-checkbox-checkmark_secondary::after {
  border-style: solid;
  border-width: 0 var(--gpx-checkbox-border-width) var(--gpx-checkbox-border-width) 0;

  @apply border-secondary;
}

/* Indeterminate styles */
.gpx-checkbox-wrapper .gpx-checkbox-checkmark_indeterminate::after {
  left: 3px;
  top: 8px;
  width: 10px;
  height: 0;
  transform: rotate(0deg);
}

.gpx-checkbox-wrapper input ~ .gpx-checkbox-checkmark_indeterminate::after {
  display: block;
}

.gpx-checkbox-checkmark_indeterminate.gpx-checkbox-checkmark_primary {
  @apply border-primary;
}

.gpx-checkbox-checkmark_indeterminate.gpx-checkbox-checkmark_secondary {
  @apply border-secondary;
}

/* Disabled styles */
.gpx-checkbox_disabled {
  cursor: not-allowed;
}

.gpx-checkbox_disabled input ~ .gpx-checkbox-checkmark {
  @apply bg-gray-400 border-gray-500;
}

.gpx-checkbox_disabled:hover input ~ .gpx-checkbox-checkmark {
  @apply border-gray-500;
}

.gpx-checkbox_disabled .gpx-checkbox-label {
  @apply text-gray-500;
}

.gpx-checkbox-hint {
  margin-top: 0.25rem;

  @apply text-gray-500 text-xs;
}
</style>
